import React, { useMemo, useRef, useState } from 'react';
import { Check, CreditCard, Pencil, RefreshCw, TrendingUp, X } from 'lucide-react';
import {
  normalizeExpenseStatus,
  parseExpenseDate,
  sumExpenses,
  sumExpensesForMonth,
} from '../../services/accountExpensesService';

/**
 * Top of the CRM client Billing tab: every subscription the client pays for (a client can have
 * several Stripe subscriptions), then a This month / Lifetime statement of revenue, expenses and
 * profit. Replaces the old seven stat boxes, which could only describe one subscription.
 *
 * Edits are saved through onSaveField(path, value) as single dotted-path writes
 * ('program.monthlyAmount', 'program.billingCycle', 'billing.monthlyExpenses',
 * 'billing.lifetimePaid'); value null clears the field. functions/mcp/expenses.js
 * (get_billing_summary) mirrors these numbers — keep the two in step.
 */

const CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const cycleLabel = (cycle) => {
  const key = String(cycle || 'monthly').toLowerCase();
  return CYCLE_OPTIONS.find((o) => o.value === key)?.label || cycle;
};

const STATUS_STYLES = {
  active: { label: 'Active', className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' },
  trialing: { label: 'Trial', className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' },
  past_due: { label: 'Past due', className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' },
  unpaid: { label: 'Unpaid', className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' },
  canceled: { label: 'Canceled', className: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700' },
  incomplete: { label: 'Incomplete', className: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700' },
};

const formatMoney = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}$${abs}`;
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const marginText = (revenue, profit) => (revenue > 0 ? `${Math.round((profit / revenue) * 100)}% margin` : null);

const isSet = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));

function StatusPill({ status }) {
  if (!status) return null;
  const key = String(status).toLowerCase();
  const style = STATUS_STYLES[key] || { label: key.replace(/_/g, ' '), className: STATUS_STYLES.canceled.className };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${style.className}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {style.label}
    </span>
  );
}

/** Click-to-edit dollar amount. Enter / blur saves, Escape cancels; blank saves null when allowBlank. */
function EditableAmount({ value, onSave, allowBlank = false, placeholder, label, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const start = () => {
    setDraft(isSet(value) ? String(value) : '');
    setEditing(true);
  };

  const commit = async () => {
    if (savingRef.current) return;
    const raw = String(draft).trim();
    if (raw === '' && !allowBlank) {
      setEditing(false);
      return;
    }
    const next = raw === '' ? null : Math.round(Number(raw) * 100) / 100;
    if (next !== null && (!Number.isFinite(next) || next < 0)) return;
    const unchanged = next === null ? !isSet(value) : isSet(value) && Number(value) === next;
    if (unchanged) {
      setEditing(false);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (error) {
      console.error(`Error saving ${label}:`, error);
      alert(`Failed to save ${label}. Please try again.`);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={start}
        className={`group inline-flex items-center gap-1.5 rounded-lg px-1.5 -mx-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 ${className}`}
        title={`Edit ${label}`}
      >
        {formatMoney(value)}
        <Pencil className="w-3.5 h-3.5 text-gray-400 opacity-60 group-hover:opacity-100" aria-hidden />
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-gray-400">$</span>
      <input
        type="number"
        min="0"
        step="0.01"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder={placeholder}
        disabled={saving}
        aria-label={label}
        className="w-28 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
        autoFocus
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={commit}
        disabled={saving}
        className="p-1 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
        title="Save"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setEditing(false)}
        disabled={saving}
        className="p-1 rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
        title="Cancel"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/** One row per Stripe subscription; a non-Stripe client gets a single manual program row. */
function buildSubscriptionRows(account) {
  const billing = account?.billing || {};
  const program = account?.program && typeof account.program === 'object' ? account.program : {};
  const subs = Array.isArray(billing.subscriptions) ? billing.subscriptions : [];
  if (subs.length) {
    return subs.map((s) => ({ ...s, source: 'stripe', primary: s.id === billing.stripeSubscriptionId }));
  }
  const tier = typeof account?.program === 'string' ? account.program : program.status;
  const label = tier && tier !== 'Not Started' ? `${tier} program` : 'Program';
  if (billing.stripeSubscriptionId) {
    // Synced before multi-subscription billing: only the primary subscription is on the account.
    return [{
      id: billing.stripeSubscriptionId,
      source: 'stripe',
      primary: true,
      legacy: true,
      label,
      status: billing.status,
      billingCycle: billing.billingCycle,
      currentPeriodEnd: billing.currentPeriodEnd,
      cancelAtPeriodEnd: billing.cancelAtPeriodEnd,
      cancelAt: billing.cancelAt,
      monthlyAmount: Number(billing.monthlyAmount) || 0,
    }];
  }
  return [{
    id: 'manual',
    source: 'manual',
    primary: true,
    label,
    status: billing.status || program.subscriptionStatus || null,
    billingCycle: program.billingCycle || billing.billingCycle || 'monthly',
    monthlyAmount: Number(program.monthlyAmount ?? billing.monthlyAmount ?? 0) || 0,
  }];
}

/** Lifetime collected: payments subcollection + embedded manual payments, deduped by invoice id. */
function computeLifetimeRevenue(account, stripePayments) {
  const fromBilling = (account?.billing?.payments || []).filter(
    (p) => !p.stripeInvoiceId && !stripePayments.some((sp) => sp.id === p.id)
  );
  const seen = new Set();
  const deduped = [...stripePayments, ...fromBilling].filter((p) => {
    if (!p.stripeInvoiceId) return true;
    if (seen.has(p.stripeInvoiceId)) return false;
    seen.add(p.stripeInvoiceId);
    return true;
  });
  if (!deduped.length) {
    return { amount: Number(account?.billing?.lifetimePaid ?? account?.billing?.lifetimeAmount ?? 0) || 0, paymentCount: 0 };
  }
  const amount = deduped
    .filter((p) => ['paid', 'succeeded'].includes(String(p.status || '').toLowerCase()))
    .reduce((sum, p) => sum + Math.abs(Number(p.amount) || 0), 0);
  return { amount, paymentCount: deduped.length };
}

function StatementCell({ children, caption, captionTone = 'muted' }) {
  return (
    <div className="px-3 py-3 text-right">
      <div className="text-base font-semibold tabular-nums">{children}</div>
      {caption && (
        <div className={`mt-0.5 text-[11px] ${captionTone === 'warn' ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {caption}
        </div>
      )}
    </div>
  );
}

const ClientBillingSummary = ({ account, stripePayments = [], expenses = [], onSaveField, onSyncStripe, syncing = false }) => {
  const billing = account?.billing || {};
  const program = account?.program && typeof account.program === 'object' ? account.program : {};

  const rows = useMemo(() => buildSubscriptionRows(account), [account]);
  const stripeRows = rows.filter((r) => r.source === 'stripe');
  const isStripe = stripeRows.length > 0;
  const subscriptionTotal = rows.reduce((sum, r) => sum + (Number(r.monthlyAmount) || 0), 0);

  // Revenue: a manual program.monthlyAmount wins over the Stripe total (same rule as Finance Reports).
  const manualRevenue = isSet(program.monthlyAmount) ? Number(program.monthlyAmount) : null;
  const stripeTotal = Number(billing.monthlyAmount) || 0;
  const monthlyRevenue = manualRevenue ?? stripeTotal;
  const revenueOverridden = isStripe && manualRevenue !== null;
  const revenueDiffers = revenueOverridden && Math.abs(manualRevenue - stripeTotal) >= 0.01;

  const lifetime = useMemo(() => computeLifetimeRevenue(account, stripePayments), [account, stripePayments]);

  const loggedThisMonth = useMemo(() => sumExpensesForMonth(expenses), [expenses]);
  const lifetimeExpenses = useMemo(() => sumExpenses(expenses), [expenses]);
  const loggedThisMonthCount = useMemo(() => {
    const now = new Date();
    return expenses.filter((e) => {
      if (normalizeExpenseStatus(e.status) !== 'paid') return false;
      const d = parseExpenseDate(e);
      return d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  }, [expenses]);
  const lifetimeExpenseCount = useMemo(
    () => expenses.filter((e) => normalizeExpenseStatus(e.status) === 'paid').length,
    [expenses]
  );

  const manualExpenses = isSet(billing.monthlyExpenses) ? Number(billing.monthlyExpenses) : null;
  const monthlyExpenses = manualExpenses ?? loggedThisMonth;

  const monthlyProfit = monthlyRevenue - monthlyExpenses;
  const lifetimeProfit = lifetime.amount - lifetimeExpenses;

  const save = (path) => (value) => onSaveField(path, value);

  const clearLink = (label, path) => (
    <button
      type="button"
      onClick={() => onSaveField(path, null).catch((e) => {
        console.error(`Error clearing ${path}:`, e);
        alert('Failed to save. Please try again.');
      })}
      className="ml-1 font-medium text-red-600 hover:text-red-700 dark:text-red-400 underline-offset-2 hover:underline"
    >
      {label}
    </button>
  );

  let revenueCaption;
  if (!isStripe) revenueCaption = 'Manual amount';
  else if (revenueDiffers) revenueCaption = <>Manual · Stripe {formatMoney(stripeTotal)}{clearLink('Use Stripe', 'program.monthlyAmount')}</>;
  else if (revenueOverridden) revenueCaption = <>Manual (matches Stripe){clearLink('Use Stripe', 'program.monthlyAmount')}</>;
  else revenueCaption = `Stripe · ${stripeRows.length} subscription${stripeRows.length === 1 ? '' : 's'}`;

  const expensesCaption = manualExpenses !== null
    ? <>Manual · logged {formatMoney(loggedThisMonth)}{clearLink('Use logged', 'billing.monthlyExpenses')}</>
    : `${loggedThisMonthCount} logged this month`;

  const profitTone = (n) => (n < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400');

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 mb-6">
      {/* Subscriptions */}
      <section className="xl:col-span-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
        <header className="relative flex items-center justify-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Subscriptions</h3>
          <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-300">
            {rows.length}
          </span>
          {onSyncStripe && account?.stripeCustomerId && (
            <button
              type="button"
              onClick={onSyncStripe}
              disabled={syncing}
              className="absolute right-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              title="Pull every subscription and payment for this client from Stripe"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} aria-hidden />
              <span className="hidden sm:inline">{syncing ? 'Syncing…' : 'Sync from Stripe'}</span>
            </button>
          )}
        </header>

        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((row) => {
            const periodDate = formatDate(row.cancelAt || row.currentPeriodEnd);
            const cycle = String(row.billingCycle || 'monthly').toLowerCase();
            return (
              <li key={row.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{row.label || 'Subscription'}</span>
                    {row.primary && stripeRows.length > 1 && (
                      <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Primary
                      </span>
                    )}
                    {row.source === 'manual' && (
                      <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Not in Stripe
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 dark:text-gray-400">
                    {row.source === 'manual' ? (
                      <select
                        value={CYCLE_OPTIONS.some((o) => o.value === cycle) ? cycle : 'monthly'}
                        onChange={(e) => onSaveField('program.billingCycle', cycleLabel(e.target.value)).catch((err) => {
                          console.error('Error saving billing cycle:', err);
                          alert('Failed to save billing cycle. Please try again.');
                        })}
                        className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-0.5 pl-1.5 pr-6 text-xs text-gray-700 dark:text-gray-200"
                        aria-label="Billing cycle"
                      >
                        {CYCLE_OPTIONS.filter((o) => o.value !== 'quarterly' || cycle === 'quarterly').map((o) => (
                          <option key={o.value} value={o.value}>Billed {o.label.toLowerCase()}</option>
                        ))}
                      </select>
                    ) : (
                      <span>Billed {cycleLabel(cycle).toLowerCase()}</span>
                    )}
                    {periodDate && <span>· {row.cancelAtPeriodEnd ? 'ends' : 'renews'} {periodDate}</span>}
                    {row.source === 'stripe' && <span className="font-mono text-[10px] text-gray-400">· {row.id}</span>}
                  </div>
                </div>
                <StatusPill status={row.status} />
                <div className="w-28 text-right">
                  <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{formatMoney(row.monthlyAmount)}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">/mo</span>
                </div>
              </li>
            );
          })}
        </ul>

        <footer className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {rows.length > 1 ? 'Combined' : 'Total'}
          </span>
          <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
            {formatMoney(subscriptionTotal)}<span className="text-xs font-normal text-gray-500 dark:text-gray-400">/mo</span>
          </span>
        </footer>

        {rows.some((r) => r.legacy) && (
          <div className="px-4 py-2 text-[11px] text-center text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800">
            Only the primary Stripe subscription is recorded yet — Sync from Stripe to pull in any others.
          </div>
        )}
      </section>

      {/* Profit & loss */}
      <section className="xl:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
        <header className="flex items-center justify-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <TrendingUp className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Profit &amp; Loss</h3>
        </header>

        <div className="grid grid-cols-[auto_1fr_1fr] text-sm">
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50" />
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">This month</div>
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Lifetime</div>

          <div className="px-3 py-3 font-medium text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800">Revenue</div>
          <div className="border-t border-gray-100 dark:border-gray-800">
            <StatementCell caption={revenueCaption} captionTone={revenueDiffers ? 'warn' : 'muted'}>
              <EditableAmount
                value={monthlyRevenue}
                onSave={save('program.monthlyAmount')}
                label="monthly revenue"
                className="text-gray-900 dark:text-white"
              />
            </StatementCell>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800">
            <StatementCell caption={lifetime.paymentCount ? `${lifetime.paymentCount} payment${lifetime.paymentCount === 1 ? '' : 's'}` : 'Manual amount'}>
              {lifetime.paymentCount ? (
                <span className="text-gray-900 dark:text-white">{formatMoney(lifetime.amount)}</span>
              ) : (
                <EditableAmount
                  value={lifetime.amount}
                  onSave={save('billing.lifetimePaid')}
                  label="lifetime revenue"
                  className="text-gray-900 dark:text-white"
                />
              )}
            </StatementCell>
          </div>

          <div className="px-3 py-3 font-medium text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800">Expenses</div>
          <div className="border-t border-gray-100 dark:border-gray-800">
            <StatementCell caption={expensesCaption}>
              <EditableAmount
                value={monthlyExpenses}
                onSave={save('billing.monthlyExpenses')}
                allowBlank
                placeholder={String(loggedThisMonth)}
                label="monthly expenses"
                className="text-red-600 dark:text-red-400"
              />
            </StatementCell>
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800">
            <StatementCell caption={`${lifetimeExpenseCount} logged`}>
              <span className="text-red-600 dark:text-red-400">{formatMoney(lifetimeExpenses)}</span>
            </StatementCell>
          </div>

          <div className="px-3 py-3 font-semibold text-gray-900 dark:text-white border-t-2 border-gray-200 dark:border-gray-700">Profit</div>
          <div className="border-t-2 border-gray-200 dark:border-gray-700">
            <StatementCell caption={marginText(monthlyRevenue, monthlyProfit)}>
              <span className={profitTone(monthlyProfit)}>{formatMoney(monthlyProfit)}</span>
            </StatementCell>
          </div>
          <div className="border-t-2 border-gray-200 dark:border-gray-700">
            <StatementCell caption={marginText(lifetime.amount, lifetimeProfit)}>
              <span className={profitTone(lifetimeProfit)}>{formatMoney(lifetimeProfit)}</span>
            </StatementCell>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientBillingSummary;
